import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where, Timestamp, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Business, BusinessDocumentData, BusinessModel, BUSINESS_CATEGORIES } from '../models/Business';
import BusinessDetailView from './BusinessDetailView';

interface BusinessVideo {
  id: string;
  businessId: string;
  title: string;
  videoUrl: string;
  isActive: boolean;
  createdAt?: { seconds: number };
  playlistOrder?: number;
}

// Mirrors iOS BusinessListView: featured video, search bar, pill
// category filter, single card-row list sorted alphabetically.
const BusinessesView: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [videos, setVideos] = useState<BusinessVideo[]>([]);
  const [videoIndex, setVideoIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Mirrors iOS BusinessViewModel.fetchBusinesses — regular users only
    // see active businesses with a valid subscription; expired ones are
    // filtered at the query level and never reach the UI.
    const businessesQuery = query(
      collection(db, 'businesses'),
      where('isActive', '==', true),
      where('subscriptionValidUntil', '>', Timestamp.now())
    );
    const unsub = onSnapshot(
      businessesQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const items = snapshot.docs.map(d => {
          const data = d.data() as BusinessDocumentData;
          data.id = d.id;
          return BusinessModel.fromFirestore(data);
        });
        setBusinesses(items);
        setIsLoading(false);
      },
      (err) => { console.error('Error loading businesses:', err); setIsLoading(false); }
    );
    return unsub;
  }, []);

  // Featured videos — mirrors iOS BusinessVideoViewModel.fetchVideos:
  // active videos, playlistOrder first then newest. Read is public.
  useEffect(() => {
    const videosQuery = query(
      collection(db, 'businessVideos'),
      where('isActive', '==', true)
    );
    const unsub = onSnapshot(
      videosQuery,
      (snapshot) => {
        const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BusinessVideo));
        items.sort((a, b) => {
          if (a.playlistOrder != null && b.playlistOrder != null) return a.playlistOrder - b.playlistOrder;
          if (a.playlistOrder != null) return -1;
          if (b.playlistOrder != null) return 1;
          return (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
        });
        setVideos(items);
      },
      (err) => console.error('Error loading videos:', err)
    );
    return unsub;
  }, []);

  const currentVideo = videos.length > 0 ? videos[videoIndex % videos.length] : null;

  const categories = ['all', ...Object.keys(BUSINESS_CATEGORIES)];

  const filtered = useMemo(() => {
    let result = businesses;
    if (filter !== 'all') result = result.filter(b => b.category === filter);
    if (searchText.trim()) {
      const t = searchText.trim().toLowerCase();
      result = result.filter(b =>
        b.name.toLowerCase().includes(t) || b.description.toLowerCase().includes(t)
      );
    }
    return [...result].sort((a, b) => a.name.localeCompare(b.name));
  }, [businesses, filter, searchText]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#f2f2f7' }}>
      {/* Featured video — mirrors iOS VideoPlayer at top of BusinessListView */}
      {currentVideo && (
        <div style={{ flexShrink: 0, backgroundColor: '#000' }}>
          <video
            key={currentVideo.id}
            ref={videoRef}
            src={currentVideo.videoUrl}
            autoPlay
            muted
            playsInline
            controls
            onEnded={() => setVideoIndex(i => i + 1)}
            style={{ width: '100%', height: '220px', objectFit: 'contain', display: 'block' }}
          />
        </div>
      )}

      {/* Search bar — iOS-style rounded field */}
      <div style={{ padding: '12px 16px 8px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          backgroundColor: '#e9e9eb', borderRadius: '10px', padding: '8px 12px'
        }}>
          <span style={{ color: '#8e8e93', fontSize: '14px' }}>🔍</span>
          <input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search businesses"
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: '15px', color: '#000'
            }}
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              style={{ border: 'none', background: 'none', color: '#8e8e93', cursor: 'pointer', fontSize: '14px', padding: 0 }}
            >✕</button>
          )}
        </div>
      </div>

      {/* Category filter pills */}
      <div style={{
        display: 'flex', gap: '8px', padding: '4px 16px 12px',
        overflowX: 'auto', flexShrink: 0, WebkitOverflowScrolling: 'touch'
      }}>
        {categories.map(c => {
          const label = c === 'all' ? 'All' : c;
          const emoji = c === 'all' ? '🌐' : BUSINESS_CATEGORIES[c as keyof typeof BUSINESS_CATEGORIES]?.emoji;
          const selected = filter === c;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              style={{
                padding: '7px 14px', borderRadius: '999px', fontSize: '13px', cursor: 'pointer',
                border: 'none', whiteSpace: 'nowrap', flexShrink: 0,
                backgroundColor: selected ? '#007aff' : 'white',
                color: selected ? 'white' : '#333',
                fontWeight: selected ? 600 : 500,
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
              }}
            >
              {emoji} {label}
            </button>
          );
        })}
      </div>

      {/* Card-row list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading businesses…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏪</div>
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#666', margin: '0 0 4px' }}>
              {searchText || filter !== 'all' ? 'No businesses found' : 'No businesses available'}
            </p>
            {(searchText || filter !== 'all') && (
              <button
                onClick={() => { setSearchText(''); setFilter('all'); }}
                style={{
                  marginTop: '12px', padding: '8px 20px', borderRadius: '999px',
                  border: 'none', backgroundColor: '#007aff', color: 'white',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer'
                }}
              >Clear Filters</button>
            )}
          </div>
        ) : (
          filtered.map(b => {
            const cat = BUSINESS_CATEGORIES[b.category];
            return (
              <div
                key={b.id}
                onClick={() => setSelectedBusiness(b)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px', marginBottom: '8px',
                  backgroundColor: 'white', borderRadius: '12px',
                  cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}
              >
                {b.imageUrl ? (
                  <img
                    src={b.imageUrl} alt={b.name}
                    style={{ width: '52px', height: '52px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: '52px', height: '52px', borderRadius: '10px', backgroundColor: '#f2f2f7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '24px', flexShrink: 0
                  }}>
                    {cat?.emoji ?? '🏪'}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <strong style={{ fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.name}
                    </strong>
                  </div>
                  <p style={{ margin: '1px 0', fontSize: '12px', color: '#8e8e93' }}>
                    {cat?.emoji} {b.category}
                  </p>
                  {b.description && (
                    <p style={{
                      margin: '2px 0 0', fontSize: '12px', color: '#666',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {b.description}
                    </p>
                  )}
                  {b.rating > 0 && (
                    <span style={{ fontSize: '11px', color: '#ff9500' }}>
                      {'★'.repeat(Math.round(b.rating))} {b.rating.toFixed(1)}
                      <span style={{ color: '#8e8e93' }}> · {b.likes} likes</span>
                    </span>
                  )}
                </div>
                <span style={{ color: '#c7c7cc', fontSize: '16px', flexShrink: 0 }}>›</span>
              </div>
            );
          })
        )}
      </div>

      {selectedBusiness?.id && (
        <BusinessDetailView
          businessId={selectedBusiness.id}
          isOpen={!!selectedBusiness}
          onClose={() => setSelectedBusiness(null)}
        />
      )}
    </div>
  );
};

export default BusinessesView;
